import React, { useState, useRef, useEffect } from 'react';
import { BWHeader, space18 } from '../dist/helpers';
import { askAI } from '../api';

const USER_COLOR_LIGHT = '#0d6efd';
const USER_COLOR_DARK  = '#4488ff';
const AI_BG_LIGHT      = 'rgba(0,0,0,0.06)';
const AI_BG_DARK       = 'rgba(255,255,255,0.08)';

// Render **bold** within a plain-text segment
const renderBold = (text) =>
  text.split(/\*\*(.*?)\*\*/g).map((part, i) =>
    i % 2 === 1 ? <strong key={i}>{part}</strong> : part
  );

// Split on ```...``` blocks first, then apply bold inside plain segments
const renderContent = (text, dark) =>
  text.split(/(```[\s\S]*?```)/g).map((part, i) => {
    if (i % 2 === 1) {
      // Strip the opening ``` (plus optional language tag on the same line) and closing ```
      const inner   = part.slice(3, -3);
      const lines   = inner.split('\n');
      const hasLang = lines[0] && /^[a-zA-Z0-9+#._-]+$/.test(lines[0].trim());
      const code    = (hasLang ? lines.slice(1) : lines)
        .join('\n')
        .replace(/^\n/, '')
        .replace(/\n$/, '');
      return (
        <pre key={i} style={{
          background   : dark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.07)',
          border       : `1px solid ${dark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.15)'}`,
          borderRadius : '6px',
          padding      : '0.7rem 1rem',
          overflowX    : 'auto',
          fontSize     : '0.82rem',
          fontFamily   : 'monospace',
          margin       : '0.5rem 0',
          whiteSpace   : 'pre',
          wordBreak    : 'normal',
          color        : dark ? '#e8e8e8' : '#222',
        }}>
          <code>{code}</code>
        </pre>
      );
    }
    return <React.Fragment key={i}>{renderBold(part)}</React.Fragment>;
  });

export default function Chat({ dark }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [model, setModel]       = useState(null);
  const [error, setError]       = useState(null);
  const msgsRef    = useRef(null);
  const inputRef   = useRef(null);

  useEffect(() => {
    const el = msgsRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, loading]);

  const handleSend = async () => {
    const text = input.trim();
    if (!text || loading) return;

    setInput('');
    setError(null);

    const userMsg = { role: 'user', content: text };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setLoading(true);

    // Build history as all prior turns (everything before this new message)
    const history = messages.map(m => ({ role: m.role, content: m.content }));

    const result = await askAI(history, text);
    setLoading(false);

    if (result.success && result.answer) {
      if (result.model) setModel(result.model);
      setMessages(prev => [...prev, { role: 'assistant', content: result.answer }]);
    } else {
      setError(result.message || 'Something went wrong. Please try again.');
      // Remove the optimistically added user message so state stays consistent
      setMessages(prev => prev.slice(0, -1));
    }

    inputRef.current?.focus();
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const clearChat = () => {
    setMessages([]);
    setError(null);
    setModel(null);
    inputRef.current?.focus();
  };

  const textColor  = dark ? '#e8e8e8' : '#222';
  const borderColor = dark ? '#444' : '#ddd';
  const bgCard     = dark ? 'rgba(33,37,41,0.85)' : 'rgba(240,248,255,0.85)';
  const inputBg    = dark ? '#1a1a1a' : '#fff';
  const accentColor = dark ? USER_COLOR_DARK : USER_COLOR_LIGHT;

  return (
    <div style={{ minHeight: '100vh' }}>
      <center>
        {BWHeader(dark, 'AI Chat', 'Ask me anything.')}
        <br />

        <div style={{
          width: '100%',
          maxWidth: 720,
          padding: '0 1rem',
          textAlign: 'left',
        }}>
          {/* Chat window */}
          <div ref={msgsRef} style={{
            background: bgCard,
            border: `1.5px solid ${borderColor}`,
            borderRadius: 16,
            padding: '1rem',
            minHeight: 420,
            maxHeight: '60vh',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          }}>
            {messages.length === 0 && !loading && (
              <div style={{
                margin: 'auto',
                color: dark ? '#666' : '#aaa',
                fontSize: '0.95rem',
                textAlign: 'center',
                userSelect: 'none',
              }}>
                Send a message to start the conversation.
              </div>
            )}

            {messages.map((msg, i) => {
              const isUser = msg.role === 'user';
              return (
                <div key={i} style={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                }}>
                  <div style={{
                    maxWidth: '80%',
                    padding: '0.6rem 1rem',
                    borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    background: isUser ? accentColor : (dark ? AI_BG_DARK : AI_BG_LIGHT),
                    color: isUser ? '#fff' : textColor,
                    fontSize: '0.95rem',
                    lineHeight: 1.55,
                    border: isUser ? 'none' : `1px solid ${borderColor}`,
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                  }}>
                    {renderContent(msg.content, dark)}
                  </div>
                </div>
              );
            })}

            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '0.6rem 1.1rem',
                  borderRadius: '18px 18px 18px 4px',
                  background: dark ? AI_BG_DARK : AI_BG_LIGHT,
                  border: `1px solid ${borderColor}`,
                  display: 'flex',
                  gap: 5,
                  alignItems: 'center',
                }}>
                  {[0, 1, 2].map(d => (
                    <span key={d} style={{
                      width: 7, height: 7,
                      borderRadius: '50%',
                      background: dark ? '#888' : '#bbb',
                      display: 'inline-block',
                      animation: `chatDot 1.2s ${d * 0.2}s infinite`,
                    }} />
                  ))}
                </div>
              </div>
            )}

          </div>

          {/* Typing dots keyframes */}
          <style>{`
            @keyframes chatDot {
              0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
              40% { opacity: 1; transform: scale(1); }
            }
          `}</style>

          {/* Error */}
          {error && (
            <div style={{
              marginTop: '0.6rem',
              padding: '0.5rem 1rem',
              borderRadius: 8,
              background: 'rgba(220,53,69,0.12)',
              border: '1px solid rgba(220,53,69,0.4)',
              color: '#dc3545',
              fontSize: '0.88rem',
            }}>
              {error}
            </div>
          )}

          {/* Input bar */}
          <div style={{
            display: 'flex',
            gap: '0.5rem',
            marginTop: '0.75rem',
            alignItems: 'flex-end',
          }}>
            <textarea
              ref={inputRef}
              rows={2}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              disabled={loading}
              placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
              style={{
                flex: 1,
                resize: 'none',
                padding: '0.6rem 1rem',
                borderRadius: 12,
                border: `1.5px solid ${loading ? borderColor : accentColor}`,
                background: inputBg,
                color: textColor,
                fontSize: '0.95rem',
                outline: 'none',
                transition: 'border-color 0.15s',
                fontFamily: 'inherit',
                lineHeight: 1.5,
              }}
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              style={{
                padding: '0.6rem 1.2rem',
                borderRadius: 12,
                border: 'none',
                background: loading || !input.trim() ? (dark ? '#333' : '#ccc') : accentColor,
                color: loading || !input.trim() ? (dark ? '#666' : '#999') : '#fff',
                fontWeight: 700,
                fontSize: '0.95rem',
                cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                transition: 'background 0.15s',
                whiteSpace: 'nowrap',
                alignSelf: 'stretch',
              }}
            >
              Send
            </button>
          </div>

          {/* Footer row */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginTop: '0.5rem',
            padding: '0 0.25rem',
          }}>
            <span style={{ fontSize: '0.72rem', color: dark ? '#555' : '#bbb' }}>
              {model ? `Model: ${model}` : ''}
            </span>
            {messages.length > 0 && (
              <button
                onClick={clearChat}
                style={{
                  background: 'none',
                  border: 'none',
                  color: dark ? '#555' : '#bbb',
                  fontSize: '0.78rem',
                  cursor: 'pointer',
                  padding: '2px 6px',
                }}
              >
                Clear chat
              </button>
            )}
          </div>
        </div>

        {space18}
      </center>
    </div>
  );
}
